import * as React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { useParams, useHistory } from "react-router-dom";
import { last } from "lodash";
import { parseApiDate } from "../../helpers/date";
import { formatGender } from "../../helpers/common";
import { IStore } from "../../resources/types";
import {
  fetchUser,
  FetchUser,
  updateUserIntro,
  UpdateUserIntro
} from "../../resources/user/user.actions";
import { getUser } from "../../resources/user/user.selectors";
import { IUpdateUserIntro, IUser, IWorkout } from "../../resources/user/types";
import Button from "../../components/button";
import Measurements from "./measurements";
import WorkoutProgress from "./workout-progress";

import Workout from "./workout";

import styles from "./user.module.css";
import IntroSpecial from "./intro-special";

interface IProps {
  user: IUser | undefined;
  fetchUser: FetchUser;
  updateUserIntro: UpdateUserIntro;
  workout: IWorkout;
}

function User({ user, fetchUser, updateUserIntro }: IProps) {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const onAddNewWorkoutClick = () =>
    history.push(`/users/${id}/workout/create`);

  React.useEffect(() => {
    fetchUser(id);
  }, [id, fetchUser]);

  if (!user) {
    return null;
  }

  async function onUpdateUserIntro(data: IUpdateUserIntro) {
    await updateUserIntro(id, data);
    await fetchUser(id);
  }

  const { gender } = last(user.profile) || {};

  return (
    <>
      <div className={styles.section}>Profile</div>
      <div className={styles.head}>
        <div className={styles.fieldContainer}>
          <div className={styles.label}>Name:</div>
          <div
            className={styles.field}
          >{`${user.firstName} ${user.lastName}`}</div>
        </div>
        <div className={styles.fieldContainer}>
          <div className={styles.label}>Email:</div>
          <div className={styles.field}>{user.email}</div>
        </div>
        <div className={styles.fieldContainer}>
          <div className={styles.label}>Phone:</div>
          <div className={styles.field}>{user.phone}</div>
        </div>
        {Boolean(user.dob) && (
          <div className={styles.fieldContainer}>
            <div className={styles.label}>DOB:</div>
            <div className={styles.field}>
              {parseApiDate(user.dob).format("MMM D, YYYY")}
            </div>
          </div>
        )}
        {Boolean(user.firstClassDate) && (
          <div className={styles.fieldContainer}>
            <div className={styles.label}>First class:</div>
            <div className={styles.field}>
              {moment(user.firstClassDate)
                .tz(user.timezone)
                .format("MMM D, YYYY")}{" "}
              -{" "}
              {moment(user.firstClassDate)
                .tz(user.timezone)
                .fromNow()}
            </div>
          </div>
        )}
        {Boolean(gender) && gender && (
          <div className={styles.fieldContainer}>
            <div className={styles.label}>Gender:</div>
            <div className={styles.field}>{formatGender(gender)}</div>
          </div>
        )}
        {user.address && (
          <div className={styles.fieldContainer}>
            <div className={styles.label}>Address:</div>
            <div className={styles.field}>{user.address}</div>
          </div>
        )}
      </div>
      {user.issueInvoiceAt && (
        <>
          <div className={styles.section}>Intro Special</div>
          <IntroSpecial user={user} onUpdateUserIntro={onUpdateUserIntro} />
        </>
      )}
      {user.profile.length > 0 ? (
        <>
          <div className={styles.section}>Measurements</div>
          <Measurements profile={user.profile} />
        </>
      ) : null}
      {user.logbook.length > 0 ? (
        <>
          <div className={styles.section}>Progress</div>
          <WorkoutProgress workouts={user.logbook} />
          <div className={styles.section}>Workouts</div>
          <div className={styles.list}>
            {user.logbook.map(workout => (
              <Workout key={workout.id} workout={workout} />
            ))}
          </div>
        </>
      ) : null}
      {user.externalId ? (
        <>
          <Button className={styles.btn} onClick={onAddNewWorkoutClick}>
            New workout
          </Button>
        </>
      ) : null}
    </>
  );
}

export default connect(
  (state: IStore) => ({
    user: getUser(state)
  }),
  {
    fetchUser,
    updateUserIntro
  }
)(User);
